import { useIntl } from 'react-intl';
import messages from '../constants/messages';
import phoneRegex from '../constants/phoneRegex';
import * as Yup from 'yup';
import { useCallback } from 'react';

// Define the custom method and its type
declare module 'yup' {
    interface ObjectSchema {
        atLeastOneOfCombined: (fields: string[], combinedField: string[]) => ObjectSchema;
    }
}

// eslint-disable-next-line func-names
Yup.addMethod(Yup.object, 'atLeastOneOfCombined', function (fields, combinedField) {
    return this.test({
        name: 'atLeastOneOfCombined',
        message() {
            const errorMessage = 'AtLeastOneOfError';
            return { atLeastOneOfCombined: errorMessage };
        },
        exclusive: true,
        params: { keys: [...fields, combinedField].join(', ') },
        test(value) {
            return (
                value == null ||
                fields.some((f: string | number) => value[f] != null) ||
                (value[combinedField[0]] != null && value[combinedField[1]] != null)
            );
        },
    });
});

const useCreateCustomerSchema = () => {
    const { formatMessage } = useIntl();
    const mandatoryMessage = formatMessage(messages.fieldError);

    return useCallback(
        (deskOrder = false, deviceId = false, requireLicensePlate = false, requireVehicleLength = false) => {
            let emailSchema: Yup.StringSchema<string | null>;
            let phoneSchema: Yup.StringSchema<string | null>;

            // In the case when doing a desk booking, the email field shouldn't be required
            if (deskOrder) {
                emailSchema = Yup.string().nullable();
                phoneSchema = Yup.string().matches(phoneRegex, formatMessage(messages.phoneNumberError)).nullable();
            } else {
                emailSchema = Yup.string().email(formatMessage(messages.emailError)).required(mandatoryMessage);
                phoneSchema = Yup.string().matches(phoneRegex, formatMessage(messages.phoneNumberError));
            }

            const licensePlateSchema = requireLicensePlate
                ? Yup.string().required(mandatoryMessage)
                : Yup.string().nullable();

            const vehicleLengthSchema = requireVehicleLength
                ? Yup.number().typeError(mandatoryMessage).required(mandatoryMessage)
                : Yup.number().nullable();

            // Return the schema created using Yup.lazy()
            return deskOrder
                ? Yup.object()
                      .nullable()
                      .shape({
                          firstName: Yup.string().nullable(),
                          lastName: Yup.string().nullable(),
                          address: Yup.object()
                              .shape({
                                  street: Yup.string().nullable(),
                                  number: Yup.string().nullable(),
                                  postalCode: Yup.string().nullable(),
                                  city: Yup.string().nullable(),
                                  countryCode: Yup.string().nullable(),
                              })
                              .nullable(),
                          email: emailSchema,
                          phoneNumber: phoneSchema,
                          licensePlate: licensePlateSchema.nullable(),
                          vehicleLength: vehicleLengthSchema.nullable(),
                      })
                      .atLeastOneOfCombined(['email', 'phoneNumber'], ['firstName', 'lastName'])
                : deviceId
                ? Yup.object().shape({
                      firstName: Yup.string().required(mandatoryMessage),
                      lastName: Yup.string().required(mandatoryMessage),
                      email: emailSchema.email(formatMessage(messages.emailError)).required(mandatoryMessage),
                      phoneNumber: phoneSchema.required(mandatoryMessage),
                      licensePlate: licensePlateSchema,
                      vehicleLength: vehicleLengthSchema,
                  })
                : Yup.object().shape({
                      firstName: Yup.string().required(mandatoryMessage),
                      lastName: Yup.string().required(mandatoryMessage),
                      address: Yup.object().shape({
                          street: Yup.string().required(mandatoryMessage),
                          number: Yup.string().required(mandatoryMessage),
                          postalCode: Yup.string().required(mandatoryMessage),
                          city: Yup.string().required(mandatoryMessage),
                          countryCode: Yup.string().required(formatMessage(messages.countryCodeError)),
                      }),
                      email: emailSchema,
                      phoneNumber: phoneSchema.required(mandatoryMessage),
                      licensePlate: licensePlateSchema,
                      vehicleLength: vehicleLengthSchema,
                  });
        },
        [formatMessage, mandatoryMessage]
    );
};

export default useCreateCustomerSchema;
